import { useEffect, useState } from 'react'
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/modules/auth/hooks/useAuth'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Menu, User } from 'lucide-react'
import { useProfile } from '@/modules/profile/hooks/useProfile'
import { useUnreadCount, useNotificationsStream, useFaviconBadge } from '@/modules/notifications'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsMobile } from '@/lib/hooks'
import { useFlag } from '@/modules/feature-flags/hooks/useFeatureFlags'
import { FeatureFlags } from '@fp3/shared'
import { APP_ENV, IS_PROD, LOGO_SRC } from '@/lib/env'
import { NavDropdown } from './NavDropdown'
import { ThemeToggle } from './ThemeToggle'

const mobileNavLinkClassName = ({ isActive }: { isActive: boolean }) =>
  `block py-2 text-sm transition-colors ${isActive ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`

const mobileSubNavLinkClassName = ({ isActive }: { isActive: boolean }) =>
  `block py-1.5 pl-5 text-sm border-l border-border ml-1 transition-colors ${isActive ? 'font-medium text-foreground' : 'font-normal text-muted-foreground'}`

const mobileGroupLabelClassName =
  'text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-4 pb-1'

interface HeaderProps {
  /** when true, the header chrome fills the full viewport width instead of the default 1440px cap — used by pages that render full-bleed bodies (e.g. Reports) so the header aligns edge-to-edge with the content below */
  wide?: boolean
}

export const Header = ({ wide = false }: HeaderProps = {}) => {
  const { user, logout, isAdmin } = useAuth()
  const { enabled: showInvestments } = useFlag(FeatureFlags.INVESTMENTS)
  const { data: profile } = useProfile()
  const { data: unreadData } = useUnreadCount()
  useNotificationsStream()
  useFaviconBadge()
  const navigate = useNavigate()
  const location = useLocation()
  const unreadCount = unreadData?.count ?? 0
  const shouldNavigate = unreadCount > 0 && location.pathname !== '/profile/notifications'
  const isMobile = useIsMobile()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false)
  }, [isMobile])

  const closeMobileMenu = () => setMobileMenuOpen(false)

  return (
    <header className="bg-background border-b border-border">
      <div className={`${wide ? 'w-full' : 'max-w-[1440px] mx-auto'} px-4 sm:px-6 lg:px-8`}>
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-4 lg:gap-6 h-full">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/" className="flex items-center" data-testid="header-logo-link">
                  <img src={LOGO_SRC} alt="FP3 Finance" className="h-9" />
                </Link>
              </TooltipTrigger>
              {!IS_PROD && (
                <TooltipContent data-testid="header-env-tooltip">
                  {APP_ENV.toUpperCase()}
                </TooltipContent>
              )}
            </Tooltip>
            {user && !isMobile && (
              <nav className="flex items-center gap-2 md:gap-4 h-full">
                <NavDropdown
                  to="/accounts"
                  label="Accounts"
                  testId="layout-nav-accounts-link"
                  items={[
                    {
                      to: '/accounts',
                      label: 'List',
                      end: true,
                      testId: 'layout-nav-accounts-list-link',
                    },
                    {
                      to: '/accounts/exchange-rates',
                      label: 'Exchange Rates',
                      testId: 'layout-nav-accounts-exchange-rates-link',
                    },
                  ]}
                />
                <NavDropdown
                  to="/expenses"
                  label="Expenses"
                  testId="layout-nav-expenses-link"
                  items={[
                    { to: '/expenses', label: 'List', end: true },
                    {
                      to: '/expenses/recurrent',
                      label: 'Recurrent',
                      testId: 'layout-nav-expenses-recurrent-link',
                    },
                    {
                      to: '/expenses/import',
                      label: 'Import',
                      testId: 'layout-nav-expenses-import-link',
                    },
                    {
                      to: '/expenses/categories',
                      label: 'Categories',
                      testId: 'layout-nav-expenses-categories-link',
                    },
                  ]}
                />
                <NavDropdown
                  to="/incomes"
                  label="Incomes"
                  testId="layout-nav-incomes-link"
                  items={[
                    { to: '/incomes', label: 'List', end: true },
                    {
                      to: '/incomes/recurrent',
                      label: 'Recurrent',
                      testId: 'layout-nav-incomes-recurrent-link',
                    },
                    {
                      to: '/incomes/import',
                      label: 'Import',
                      testId: 'layout-nav-incomes-import-link',
                    },
                    {
                      to: '/incomes/categories',
                      label: 'Categories',
                      testId: 'layout-nav-incomes-categories-link',
                    },
                  ]}
                />
                <NavLink
                  to="/transfers"
                  data-testid="layout-nav-transfers-link"
                  className={({ isActive }) =>
                    `text-sm transition-colors ${isActive ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground hover:text-foreground'}`
                  }
                >
                  Transfers
                </NavLink>
                <NavLink
                  to="/groups"
                  data-testid="layout-nav-groups-link"
                  className={({ isActive }) =>
                    `text-sm transition-colors ${isActive ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground hover:text-foreground'}`
                  }
                >
                  Groups
                </NavLink>
                {showInvestments && (
                  <NavLink
                    to="/investments"
                    data-testid="layout-nav-investments-link"
                    className={({ isActive }) =>
                      `text-sm transition-colors ${isActive ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground hover:text-foreground'}`
                    }
                  >
                    Investments
                  </NavLink>
                )}
                <NavDropdown
                  to="/reports/cash-flow"
                  label="Reports"
                  testId="layout-nav-reports-link"
                  items={[
                    {
                      to: '/reports/cash-flow',
                      label: 'Cash Flow',
                      testId: 'layout-nav-reports-cash-flow-link',
                    },
                    {
                      to: '/reports/deposits',
                      label: 'Deposits',
                      testId: 'layout-nav-reports-deposits-link',
                    },
                    {
                      to: '/reports/expenses-by-category',
                      label: 'Expenses by Category',
                      testId: 'layout-nav-reports-expenses-by-category-link',
                    },
                    {
                      to: '/reports/expense-composition',
                      label: 'Expense Composition',
                      testId: 'layout-nav-reports-expense-composition-link',
                    },
                    {
                      to: '/reports/incomes-by-category',
                      label: 'Incomes by Category',
                      testId: 'layout-nav-reports-incomes-by-category-link',
                    },
                  ]}
                />
                {isAdmin && (
                  <NavDropdown
                    to="/admin/feature-flags"
                    label="Admin"
                    testId="layout-nav-admin-link"
                    items={[
                      {
                        to: '/admin/feature-flags',
                        label: 'Feature Flags',
                        testId: 'layout-nav-admin-feature-flags-link',
                      },
                    ]}
                  />
                )}
              </nav>
            )}
          </div>

          <div className="flex items-center gap-2">
            {user && isMobile && (
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SheetTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Menu"
                        data-testid="layout-mobile-menu-button"
                      >
                        <Menu className="h-5 w-5" />
                      </Button>
                    </SheetTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Menu</TooltipContent>
                </Tooltip>
                <SheetContent
                  side="left"
                  className="w-screen max-w-none sm:max-w-sm flex flex-col"
                  data-testid="layout-mobile-menu-sheet"
                >
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  <nav className="flex flex-col mt-4 flex-1 overflow-y-auto">
                    <p className={mobileGroupLabelClassName}>Accounts</p>
                    <NavLink
                      to="/accounts"
                      end
                      onClick={closeMobileMenu}
                      className={mobileNavLinkClassName}
                    >
                      Accounts
                    </NavLink>
                    <NavLink
                      to="/accounts/exchange-rates"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Exchange Rates
                    </NavLink>

                    <p className={mobileGroupLabelClassName}>Expenses</p>
                    <NavLink
                      to="/expenses"
                      end
                      onClick={closeMobileMenu}
                      className={mobileNavLinkClassName}
                    >
                      Expenses
                    </NavLink>
                    <NavLink
                      to="/expenses/recurrent"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Recurrent Expenses
                    </NavLink>
                    <NavLink
                      to="/expenses/import"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Import Expenses
                    </NavLink>
                    <NavLink
                      to="/expenses/categories"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Expense Categories
                    </NavLink>

                    <p className={mobileGroupLabelClassName}>Incomes</p>
                    <NavLink
                      to="/incomes"
                      end
                      onClick={closeMobileMenu}
                      className={mobileNavLinkClassName}
                    >
                      Incomes
                    </NavLink>
                    <NavLink
                      to="/incomes/recurrent"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                      data-testid="layout-nav-incomes-recurrent-link"
                    >
                      Recurrent Incomes
                    </NavLink>
                    <NavLink
                      to="/incomes/import"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Import Incomes
                    </NavLink>
                    <NavLink
                      to="/incomes/categories"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Income Categories
                    </NavLink>

                    <p className={mobileGroupLabelClassName}>Other</p>
                    <NavLink
                      to="/transfers"
                      onClick={closeMobileMenu}
                      className={mobileNavLinkClassName}
                    >
                      Transfers
                    </NavLink>
                    <NavLink
                      to="/groups"
                      onClick={closeMobileMenu}
                      className={mobileNavLinkClassName}
                    >
                      Groups
                    </NavLink>
                    <p className={mobileGroupLabelClassName}>Reports</p>
                    <NavLink
                      to="/reports/cash-flow"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Cash Flow
                    </NavLink>
                    <NavLink
                      to="/reports/deposits"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Deposits
                    </NavLink>
                    <NavLink
                      to="/reports/expenses-by-category"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Expenses by Category
                    </NavLink>
                    <NavLink
                      to="/reports/expense-composition"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Expense Composition
                    </NavLink>
                    <NavLink
                      to="/reports/incomes-by-category"
                      onClick={closeMobileMenu}
                      className={mobileSubNavLinkClassName}
                    >
                      Incomes by Category
                    </NavLink>

                    {showInvestments && (
                      <>
                        <p className={mobileGroupLabelClassName}>Investments</p>
                        <NavLink
                          to="/investments"
                          onClick={closeMobileMenu}
                          className={mobileNavLinkClassName}
                        >
                          Investments
                        </NavLink>
                        <NavLink
                          to="/settings/investments/tbank"
                          onClick={closeMobileMenu}
                          className={mobileSubNavLinkClassName}
                        >
                          T-Bank Connections
                        </NavLink>
                      </>
                    )}
                    {isAdmin && (
                      <>
                        <p className={mobileGroupLabelClassName}>Admin</p>
                        <NavLink
                          to="/admin/feature-flags"
                          onClick={closeMobileMenu}
                          className={mobileSubNavLinkClassName}
                        >
                          Feature Flags
                        </NavLink>
                      </>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            )}
            <ThemeToggle />
            {user && (
              <>
                {shouldNavigate ? (
                  <Link
                    to="/profile/notifications"
                    className="rounded-full focus:outline-none focus:ring-2 focus:ring-ring relative inline-block"
                    data-testid="header-avatar-trigger"
                  >
                    <Avatar className="h-8 w-8 cursor-pointer ring-1 ring-border">
                      <AvatarImage src={profile?.avatar ?? undefined} alt={user.name} />
                      <AvatarFallback>
                        <User className="h-4 w-4 text-muted-foreground" />
                      </AvatarFallback>
                    </Avatar>
                    <span
                      className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white"
                      data-testid="layout-notification-badge"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  </Link>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="rounded-full focus:outline-none focus:ring-2 focus:ring-ring relative"
                        data-testid="header-avatar-trigger"
                      >
                        <Avatar className="h-8 w-8 cursor-pointer ring-1 ring-border">
                          <AvatarImage src={profile?.avatar ?? undefined} alt={user.name} />
                          <AvatarFallback>
                            <User className="h-4 w-4 text-muted-foreground" />
                          </AvatarFallback>
                        </Avatar>
                        {unreadCount > 0 && (
                          <span
                            className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white"
                            data-testid="layout-notification-badge"
                          >
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/profile')}>
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/profile/notifications')}>
                        Notifications
                        {unreadCount > 0 && (
                          <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={logout} data-testid="layout-logout-menuitem">
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
